/**
 * OutgoingWebhooks Page - Manage outgoing webhooks for n8n and other integrations
 */
import React, { useState, useEffect } from 'react'
import {
  Plus,
  Search,
  Play,
  Pause,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  AlertTriangle,
  Send,
  Trash2,
  Edit2,
  Eye,
  X,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Copy,
  Zap,
  History,
  ArrowLeft,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import {
  outgoingWebhooksApi,
  OutgoingWebhook,
  WebhookDelivery,
  EventOption,
  CreateOutgoingWebhook,
} from '../services/outgoingWebhooksApi'

const OutgoingWebhooks: React.FC = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [webhooks, setWebhooks] = useState<OutgoingWebhook[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeliveriesModal, setShowDeliveriesModal] = useState(false)
  const [selectedWebhook, setSelectedWebhook] = useState<OutgoingWebhook | null>(null)
  const [availableEvents, setAvailableEvents] = useState<EventOption[]>([])
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([])
  const [loadingDeliveries, setLoadingDeliveries] = useState(false)
  const [testing, setTesting] = useState<string | null>(null)
  const [savingWebhook, setSavingWebhook] = useState(false)

  // Form state
  const [formData, setFormData] = useState<CreateOutgoingWebhook>({
    name: '',
    url: '',
    events: [],
    headers: {},
    secret: '',
    is_active: true,
    retry_count: 3,
    timeout_seconds: 30,
  })
  const [headersText, setHeadersText] = useState('{}')

  useEffect(() => {
    loadWebhooks()
    loadAvailableEvents()
  }, [])

  const loadWebhooks = async () => {
    setLoading(true)
    try {
      const data = await outgoingWebhooksApi.list({
        is_active: statusFilter === 'all' ? undefined : statusFilter === 'active',
      })
      setWebhooks(data)
    } catch (error) {
      console.error('Error loading webhooks:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadAvailableEvents = async () => {
    try {
      const events = await outgoingWebhooksApi.getAvailableEvents()
      setAvailableEvents(events)
    } catch (error) {
      console.error('Error loading events:', error)
    }
  }

  const loadDeliveries = async (webhookId: string) => {
    setLoadingDeliveries(true)
    try {
      const data = await outgoingWebhooksApi.getDeliveries({ webhook: webhookId })
      setDeliveries(data)
    } catch (error) {
      console.error('Error loading deliveries:', error)
    } finally {
      setLoadingDeliveries(false)
    }
  }

  const handleCreateWebhook = async (e: React.FormEvent) => {
    e.preventDefault()
    setSavingWebhook(true)

    try {
      let headers = {}
      try {
        headers = JSON.parse(headersText)
      } catch {
        alert('Headers invalidos. Use formato JSON valido.')
        setSavingWebhook(false)
        return
      }

      await outgoingWebhooksApi.create({
        ...formData,
        headers,
      })

      setShowCreateModal(false)
      resetForm()
      loadWebhooks()
    } catch (error) {
      console.error('Error creating webhook:', error)
      alert('Erro ao criar webhook. Verifique os dados e tente novamente.')
    } finally {
      setSavingWebhook(false)
    }
  }

  const handleUpdateWebhook = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedWebhook) return
    setSavingWebhook(true)

    try {
      let headers = {}
      try {
        headers = JSON.parse(headersText)
      } catch {
        alert('Headers invalidos. Use formato JSON valido.')
        setSavingWebhook(false)
        return
      }

      await outgoingWebhooksApi.update(selectedWebhook.id, {
        ...formData,
        headers,
      })

      setShowEditModal(false)
      resetForm()
      loadWebhooks()
    } catch (error) {
      console.error('Error updating webhook:', error)
      alert('Erro ao atualizar webhook.')
    } finally {
      setSavingWebhook(false)
    }
  }

  const handleDeleteWebhook = async (webhook: OutgoingWebhook) => {
    if (!confirm(`Tem certeza que deseja excluir o webhook "${webhook.name}"?`)) return

    try {
      await outgoingWebhooksApi.delete(webhook.id)
      loadWebhooks()
    } catch (error) {
      console.error('Error deleting webhook:', error)
      alert('Erro ao excluir webhook.')
    }
  }

  const handleToggleActive = async (webhook: OutgoingWebhook) => {
    try {
      await outgoingWebhooksApi.toggleActive(webhook.id)
      loadWebhooks()
    } catch (error) {
      console.error('Error toggling webhook:', error)
    }
  }

  const handleTestWebhook = async (webhook: OutgoingWebhook) => {
    setTesting(webhook.id)
    try {
      const result = await outgoingWebhooksApi.test(webhook.id)
      alert(result.message)
      loadWebhooks()
    } catch (error) {
      console.error('Error testing webhook:', error)
      alert('Erro ao testar webhook.')
    } finally {
      setTesting(null)
    }
  }

  const handleRetryDelivery = async (deliveryId: string) => {
    try {
      const result = await outgoingWebhooksApi.retryDelivery(deliveryId)
      alert(result.message)
      if (selectedWebhook) {
        loadDeliveries(selectedWebhook.id)
      }
    } catch (error) {
      console.error('Error retrying delivery:', error)
      alert('Erro ao reenviar webhook.')
    }
  }

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text)
    alert('Copiado!')
  }

  const openEditModal = (webhook: OutgoingWebhook) => {
    setSelectedWebhook(webhook)
    setFormData({
      name: webhook.name,
      url: webhook.url,
      events: webhook.events,
      headers: webhook.headers,
      secret: webhook.secret || '',
      is_active: webhook.is_active,
      retry_count: webhook.retry_count,
      timeout_seconds: webhook.timeout_seconds,
    })
    setHeadersText(JSON.stringify(webhook.headers, null, 2))
    setShowEditModal(true)
  }

  const openDeliveriesModal = (webhook: OutgoingWebhook) => {
    setSelectedWebhook(webhook)
    loadDeliveries(webhook.id)
    setShowDeliveriesModal(true)
  }

  const resetForm = () => {
    setFormData({
      name: '',
      url: '',
      events: [],
      headers: {},
      secret: '',
      is_active: true,
      retry_count: 3,
      timeout_seconds: 30,
    })
    setHeadersText('{}')
    setSelectedWebhook(null)
  }

  const toggleEvent = (eventValue: string) => {
    setFormData((prev) => ({
      ...prev,
      events: prev.events.includes(eventValue)
        ? prev.events.filter((e) => e !== eventValue)
        : [...prev.events, eventValue],
    }))
  }

  const filteredWebhooks = webhooks.filter((w) => {
    const matchesSearch =
      w.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      w.url.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && w.is_active) ||
      (statusFilter === 'inactive' && !w.is_active)
    return matchesSearch && matchesStatus
  })

  const getStatusBadge = (isActive: boolean) => {
    return isActive ? (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '4px 12px',
          borderRadius: '9999px',
          fontSize: 'var(--text-xs)',
          fontWeight: '500',
          background: 'var(--color-success-50)',
          color: 'var(--color-success-700)',
          border: '1px solid var(--color-success-200)',
        }}
      >
        <CheckCircle size={14} />
        Ativo
      </span>
    ) : (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '4px 12px',
          borderRadius: '9999px',
          fontSize: 'var(--text-xs)',
          fontWeight: '500',
          background: 'var(--color-gray-100)',
          color: 'var(--color-gray-700)',
          border: '1px solid var(--color-gray-200)',
        }}
      >
        <XCircle size={14} />
        Inativo
      </span>
    )
  }

  const getDeliveryStatusBadge = (status: string) => {
    const styles: Record<string, { bg: string; color: string; border: string }> = {
      success: {
        bg: 'var(--color-success-50)',
        color: 'var(--color-success-700)',
        border: 'var(--color-success-200)',
      },
      failed: {
        bg: 'var(--color-error-50)',
        color: 'var(--color-error-700)',
        border: 'var(--color-error-200)',
      },
      pending: {
        bg: 'var(--color-warning-50)',
        color: 'var(--color-warning-700)',
        border: 'var(--color-warning-200)',
      },
      retrying: {
        bg: 'var(--color-primary-50)',
        color: 'var(--color-primary-700)',
        border: 'var(--color-primary-200)',
      },
    }
    const style = styles[status] || styles.pending
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '4px 12px',
          borderRadius: '9999px',
          fontSize: 'var(--text-xs)',
          fontWeight: '500',
          background: style.bg,
          color: style.color,
          border: `1px solid ${style.border}`,
        }}
      >
        {status === 'success' && <CheckCircle size={14} />}
        {status === 'failed' && <XCircle size={14} />}
        {status === 'pending' && <Clock size={14} />}
        {status === 'retrying' && <RefreshCw size={14} />}
        {status === 'success' ? 'Sucesso' : status === 'failed' ? 'Falha' : status === 'pending' ? 'Pendente' : 'Tentando'}
      </span>
    )
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <div
          style={{
            width: '48px',
            height: '48px',
            border: '3px solid var(--glass-border)',
            borderTopColor: 'var(--color-primary-600)',
            borderRadius: '50%',
            animation: 'spin 0.6s linear infinite',
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    )
  }

  return (
    <div>
      {/* Back to Settings */}
      <button
        onClick={() => navigate('/settings')}
        style={{
          display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--text-secondary)', fontSize: 'var(--text-sm)',
          padding: 'var(--space-1) 0', marginBottom: 'var(--space-4)',
          transition: 'color 0.2s'
        }}
        onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--primary-600)')}
        onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-secondary)')}
      >
        <ArrowLeft size={16} />
        Voltar para Configurações
      </button>

      {/* Header */}
      <div style={{ marginBottom: 'var(--space-6)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-2)' }}>
          <div
            style={{
              padding: 'var(--space-2)',
              background: 'var(--color-primary-50)',
              borderRadius: 'var(--radius-lg)',
            }}
          >
            <Send size={24} style={{ color: 'var(--color-primary-600)' }} />
          </div>
          <div>
            <h1 style={{ fontSize: 'var(--text-2xl)', fontWeight: 'bold', color: 'var(--text-primary)' }}>
              Webhooks de Saida
            </h1>
            <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>
              Configure webhooks para enviar eventos do FLIPR para sistemas externos como n8n
            </p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 'var(--space-4)',
          marginBottom: 'var(--space-6)',
        }}
      >
        <div
          style={{
            background: 'var(--glass-bg-strong)',
            backdropFilter: 'var(--glass-blur)',
            borderRadius: 'var(--radius-xl)',
            border: '1px solid var(--glass-border)',
            padding: 'var(--space-4)',
            boxShadow: 'var(--shadow-glass)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', fontWeight: '500' }}>Total</p>
              <p style={{ fontSize: 'var(--text-2xl)', fontWeight: 'bold', color: 'var(--text-primary)', marginTop: '4px' }}>
                {webhooks.length}
              </p>
            </div>
            <div style={{ padding: 'var(--space-2)', background: 'var(--color-primary-50)', borderRadius: 'var(--radius-lg)' }}>
              <Zap size={20} style={{ color: 'var(--color-primary-600)' }} />
            </div>
          </div>
        </div>

        <div
          style={{
            background: 'var(--glass-bg-strong)',
            backdropFilter: 'var(--glass-blur)',
            borderRadius: 'var(--radius-xl)',
            border: '1px solid var(--glass-border)',
            padding: 'var(--space-4)',
            boxShadow: 'var(--shadow-glass)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', fontWeight: '500' }}>Ativos</p>
              <p style={{ fontSize: 'var(--text-2xl)', fontWeight: 'bold', color: 'var(--text-primary)', marginTop: '4px' }}>
                {webhooks.filter((w) => w.is_active).length}
              </p>
            </div>
            <div style={{ padding: 'var(--space-2)', background: 'var(--color-success-50)', borderRadius: 'var(--radius-lg)' }}>
              <CheckCircle size={20} style={{ color: 'var(--color-success-600)' }} />
            </div>
          </div>
        </div>

        <div
          style={{
            background: 'var(--glass-bg-strong)',
            backdropFilter: 'var(--glass-blur)',
            borderRadius: 'var(--radius-xl)',
            border: '1px solid var(--glass-border)',
            padding: 'var(--space-4)',
            boxShadow: 'var(--shadow-glass)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', fontWeight: '500' }}>Sucessos</p>
              <p style={{ fontSize: 'var(--text-2xl)', fontWeight: 'bold', color: 'var(--color-success-600)', marginTop: '4px' }}>
                {webhooks.reduce((sum, w) => sum + w.success_count, 0)}
              </p>
            </div>
            <div style={{ padding: 'var(--space-2)', background: 'var(--color-success-50)', borderRadius: 'var(--radius-lg)' }}>
              <Send size={20} style={{ color: 'var(--color-success-600)' }} />
            </div>
          </div>
        </div>

        <div
          style={{
            background: 'var(--glass-bg-strong)',
            backdropFilter: 'var(--glass-blur)',
            borderRadius: 'var(--radius-xl)',
            border: '1px solid var(--glass-border)',
            padding: 'var(--space-4)',
            boxShadow: 'var(--shadow-glass)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', fontWeight: '500' }}>Falhas</p>
              <p style={{ fontSize: 'var(--text-2xl)', fontWeight: 'bold', color: 'var(--color-error-600)', marginTop: '4px' }}>
                {webhooks.reduce((sum, w) => sum + w.failure_count, 0)}
              </p>
            </div>
            <div style={{ padding: 'var(--space-2)', background: 'var(--color-error-50)', borderRadius: 'var(--radius-lg)' }}>
              <AlertTriangle size={20} style={{ color: 'var(--color-error-600)' }} />
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div
        style={{
          background: 'var(--glass-bg-strong)',
          backdropFilter: 'var(--glass-blur)',
          borderRadius: 'var(--radius-xl)',
          border: '1px solid var(--glass-border)',
          padding: 'var(--space-4)',
          marginBottom: 'var(--space-6)',
          boxShadow: 'var(--shadow-glass)',
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-4)', alignItems: 'center', justifyContent: 'space-between' }}>
          {/* Search */}
          <div style={{ position: 'relative', flex: '1', minWidth: '200px', maxWidth: '400px' }}>
            <Search
              size={20}
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--text-muted)',
              }}
            />
            <input
              type="text"
              placeholder="Buscar webhooks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                paddingLeft: '40px',
                paddingRight: 'var(--space-4)',
                paddingTop: 'var(--space-2)',
                paddingBottom: 'var(--space-2)',
                border: '1px solid var(--border-light)',
                borderRadius: 'var(--radius-lg)',
                background: 'var(--bg-primary)',
                color: 'var(--text-primary)',
                fontSize: 'var(--text-sm)',
              }}
            />
          </div>

          {/* Filters & Actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              style={{
                padding: 'var(--space-2) var(--space-3)',
                border: '1px solid var(--border-light)',
                borderRadius: 'var(--radius-lg)',
                background: 'var(--bg-primary)',
                color: 'var(--text-primary)',
                fontSize: 'var(--text-sm)',
              }}
            >
              <option value="all">Todos</option>
              <option value="active">Ativos</option>
              <option value="inactive">Inativos</option>
            </select>

            <button
              onClick={() => setShowCreateModal(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                padding: 'var(--space-2) var(--space-4)',
                background: 'var(--color-primary-600)',
                color: 'white',
                border: 'none',
                borderRadius: 'var(--radius-lg)',
                fontSize: 'var(--text-sm)',
                fontWeight: '500',
                cursor: 'pointer',
              }}
            >
              <Plus size={18} />
              Novo Webhook
            </button>
          </div>
        </div>
      </div>

      {/* Webhooks List */}
      <div
        style={{
          background: 'var(--glass-bg-strong)',
          backdropFilter: 'var(--glass-blur)',
          borderRadius: 'var(--radius-xl)',
          border: '1px solid var(--glass-border)',
          boxShadow: 'var(--shadow-glass)',
          overflow: 'hidden',
        }}
      >
        {filteredWebhooks.length === 0 ? (
          <div style={{ padding: 'var(--space-12)', textAlign: 'center' }}>
            <div
              style={{
                margin: '0 auto',
                width: '64px',
                height: '64px',
                background: 'var(--color-gray-100)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 'var(--space-4)',
              }}
            >
              <Send size={32} style={{ color: 'var(--text-muted)' }} />
            </div>
            <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: '600', color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
              Nenhum webhook encontrado
            </h3>
            <p style={{ color: 'var(--text-secondary)', marginBottom: 'var(--space-4)' }}>
              Comece criando seu primeiro webhook para integrar com n8n ou outros sistemas
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              style={{
                padding: 'var(--space-2) var(--space-4)',
                background: 'var(--color-primary-600)',
                color: 'white',
                border: 'none',
                borderRadius: 'var(--radius-lg)',
                fontSize: 'var(--text-sm)',
                fontWeight: '500',
                cursor: 'pointer',
              }}
            >
              Criar Webhook
            </button>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg-tertiary)' }}>
                <th style={{ padding: 'var(--space-3) var(--space-4)', textAlign: 'left', fontSize: 'var(--text-xs)', fontWeight: '500', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>
                  Nome
                </th>
                <th style={{ padding: 'var(--space-3) var(--space-4)', textAlign: 'left', fontSize: 'var(--text-xs)', fontWeight: '500', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>
                  Status
                </th>
                <th style={{ padding: 'var(--space-3) var(--space-4)', textAlign: 'left', fontSize: 'var(--text-xs)', fontWeight: '500', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>
                  Eventos
                </th>
                <th style={{ padding: 'var(--space-3) var(--space-4)', textAlign: 'left', fontSize: 'var(--text-xs)', fontWeight: '500', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>
                  Sucesso/Falha
                </th>
                <th style={{ padding: 'var(--space-3) var(--space-4)', textAlign: 'left', fontSize: 'var(--text-xs)', fontWeight: '500', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>
                  Ultimo Disparo
                </th>
                <th style={{ padding: 'var(--space-3) var(--space-4)', textAlign: 'right', fontSize: 'var(--text-xs)', fontWeight: '500', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>
                  Acoes
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredWebhooks.map((webhook) => (
                <tr key={webhook.id} style={{ borderTop: '1px solid var(--border-light)' }}>
                  <td style={{ padding: 'var(--space-4)' }}>
                    <div>
                      <div style={{ fontWeight: '500', color: 'var(--text-primary)' }}>{webhook.name}</div>
                      <div
                        style={{
                          fontSize: 'var(--text-xs)',
                          color: 'var(--text-muted)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          marginTop: '4px',
                        }}
                      >
                        <ExternalLink size={12} />
                        {webhook.url.length > 50 ? webhook.url.substring(0, 50) + '...' : webhook.url}
                        <button
                          onClick={() => handleCopy(webhook.url)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: 'var(--text-muted)',
                            padding: '2px',
                          }}
                        >
                          <Copy size={12} />
                        </button>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: 'var(--space-4)' }}>{getStatusBadge(webhook.is_active)}</td>
                  <td style={{ padding: 'var(--space-4)' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {webhook.events.slice(0, 2).map((event) => (
                        <span
                          key={event}
                          style={{
                            padding: '2px 8px',
                            background: 'var(--color-primary-50)',
                            color: 'var(--color-primary-700)',
                            borderRadius: 'var(--radius-sm)',
                            fontSize: 'var(--text-xs)',
                          }}
                        >
                          {event}
                        </span>
                      ))}
                      {webhook.events.length > 2 && (
                        <span
                          style={{
                            padding: '2px 8px',
                            background: 'var(--color-gray-100)',
                            color: 'var(--text-secondary)',
                            borderRadius: 'var(--radius-sm)',
                            fontSize: 'var(--text-xs)',
                          }}
                        >
                          +{webhook.events.length - 2}
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: 'var(--space-4)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                      <span style={{ color: 'var(--color-success-600)', fontWeight: '500' }}>{webhook.success_count}</span>
                      <span style={{ color: 'var(--text-muted)' }}>/</span>
                      <span style={{ color: 'var(--color-error-600)', fontWeight: '500' }}>{webhook.failure_count}</span>
                    </div>
                  </td>
                  <td style={{ padding: 'var(--space-4)', color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>
                    {webhook.last_triggered_at ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Clock size={14} />
                        {new Date(webhook.last_triggered_at).toLocaleString('pt-BR')}
                      </div>
                    ) : (
                      <span style={{ color: 'var(--text-muted)' }}>Nunca</span>
                    )}
                  </td>
                  <td style={{ padding: 'var(--space-4)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 'var(--space-1)' }}>
                      <button
                        onClick={() => openDeliveriesModal(webhook)}
                        style={{
                          padding: 'var(--space-2)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: 'var(--text-secondary)',
                          borderRadius: 'var(--radius-md)',
                        }}
                        title="Ver historico"
                      >
                        <History size={18} />
                      </button>
                      <button
                        onClick={() => handleTestWebhook(webhook)}
                        disabled={testing === webhook.id}
                        style={{
                          padding: 'var(--space-2)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: 'var(--color-success-600)',
                          borderRadius: 'var(--radius-md)',
                        }}
                        title="Testar"
                      >
                        {testing === webhook.id ? <RefreshCw size={18} className="animate-spin" /> : <Play size={18} />}
                      </button>
                      <button
                        onClick={() => openEditModal(webhook)}
                        style={{
                          padding: 'var(--space-2)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: 'var(--color-primary-600)',
                          borderRadius: 'var(--radius-md)',
                        }}
                        title="Editar"
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        onClick={() => handleToggleActive(webhook)}
                        style={{
                          padding: 'var(--space-2)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: webhook.is_active ? 'var(--color-warning-600)' : 'var(--color-success-600)',
                          borderRadius: 'var(--radius-md)',
                        }}
                        title={webhook.is_active ? 'Desativar' : 'Ativar'}
                      >
                        {webhook.is_active ? <Pause size={18} /> : <Play size={18} />}
                      </button>
                      <button
                        onClick={() => handleDeleteWebhook(webhook)}
                        style={{
                          padding: 'var(--space-2)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: 'var(--color-error-600)',
                          borderRadius: 'var(--radius-md)',
                        }}
                        title="Excluir"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Info Banner */}
      <div
        style={{
          marginTop: 'var(--space-6)',
          padding: 'var(--space-4)',
          background: 'var(--glass-bg)',
          backdropFilter: 'var(--glass-blur)',
          border: '1px solid var(--color-primary-200)',
          borderRadius: 'var(--radius-xl)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)' }}>
          <AlertTriangle size={20} style={{ color: 'var(--color-primary-600)', marginTop: '2px' }} />
          <div>
            <h4 style={{ fontWeight: '500', color: 'var(--text-primary)', marginBottom: '4px' }}>Integracao com n8n</h4>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
              Configure a URL do seu webhook n8n para receber eventos do FLIPR em tempo real.
              Voce pode usar os eventos para disparar fluxos automatizados como envio de emails,
              atualizacoes em outras plataformas, notificacoes e muito mais.
            </p>
          </div>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
            padding: 'var(--space-4)',
          }}
        >
          <div
            style={{
              background: 'var(--bg-primary)',
              borderRadius: 'var(--radius-xl)',
              width: '100%',
              maxWidth: '600px',
              maxHeight: '90vh',
              overflow: 'auto',
            }}
          >
            <div
              style={{
                padding: 'var(--space-6)',
                borderBottom: '1px solid var(--border-light)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <h2 style={{ fontSize: 'var(--text-xl)', fontWeight: '600', color: 'var(--text-primary)' }}>
                {showEditModal ? 'Editar Webhook' : 'Criar Novo Webhook'}
              </h2>
              <button
                onClick={() => {
                  setShowCreateModal(false)
                  setShowEditModal(false)
                  resetForm()
                }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}
              >
                <X size={24} />
              </button>
            </div>
            <form onSubmit={showEditModal ? handleUpdateWebhook : handleCreateWebhook} style={{ padding: 'var(--space-6)' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                <div>
                  <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: '500', color: 'var(--text-primary)', marginBottom: '6px' }}>
                    Nome *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ex: Webhook n8n - Novos Deals"
                    style={{
                      width: '100%',
                      padding: 'var(--space-3)',
                      border: '1px solid var(--border-light)',
                      borderRadius: 'var(--radius-lg)',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      fontSize: 'var(--text-sm)',
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: '500', color: 'var(--text-primary)', marginBottom: '6px' }}>
                    URL do Webhook *
                  </label>
                  <input
                    type="url"
                    required
                    value={formData.url}
                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                    placeholder="https://n8n.seu-dominio.com/webhook/..."
                    style={{
                      width: '100%',
                      padding: 'var(--space-3)',
                      border: '1px solid var(--border-light)',
                      borderRadius: 'var(--radius-lg)',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      fontSize: 'var(--text-sm)',
                      fontFamily: 'monospace',
                    }}
                  />
                  <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginTop: '4px' }}>
                    Cole a URL do webhook gerado pelo n8n ou outro sistema
                  </p>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: '500', color: 'var(--text-primary)', marginBottom: '6px' }}>
                    Eventos *
                  </label>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      gap: 'var(--space-2)',
                      padding: 'var(--space-3)',
                      border: '1px solid var(--border-light)',
                      borderRadius: 'var(--radius-lg)',
                      maxHeight: '200px',
                      overflow: 'auto',
                    }}
                  >
                    {availableEvents.map((event) => (
                      <label
                        key={event.value}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--space-2)',
                          cursor: 'pointer',
                          padding: '6px',
                          borderRadius: 'var(--radius-md)',
                          background: formData.events.includes(event.value) ? 'var(--color-primary-50)' : 'transparent',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={formData.events.includes(event.value)}
                          onChange={() => toggleEvent(event.value)}
                        />
                        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-primary)' }}>{event.label}</span>
                      </label>
                    ))}
                  </div>
                  {formData.events.length === 0 && (
                    <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-error-600)', marginTop: '4px' }}>
                      Selecione pelo menos um evento
                    </p>
                  )}
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: '500', color: 'var(--text-primary)', marginBottom: '6px' }}>
                    Headers Customizados (JSON)
                  </label>
                  <textarea
                    value={headersText}
                    onChange={(e) => setHeadersText(e.target.value)}
                    placeholder='{"Authorization": "Bearer token"}'
                    rows={3}
                    style={{
                      width: '100%',
                      padding: 'var(--space-3)',
                      border: '1px solid var(--border-light)',
                      borderRadius: 'var(--radius-lg)',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      fontSize: 'var(--text-sm)',
                      fontFamily: 'monospace',
                      resize: 'vertical',
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: '500', color: 'var(--text-primary)', marginBottom: '6px' }}>
                    Secret (opcional - para assinatura HMAC)
                  </label>
                  <input
                    type="text"
                    value={formData.secret || ''}
                    onChange={(e) => setFormData({ ...formData, secret: e.target.value })}
                    placeholder="Chave secreta para validacao"
                    style={{
                      width: '100%',
                      padding: 'var(--space-3)',
                      border: '1px solid var(--border-light)',
                      borderRadius: 'var(--radius-lg)',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      fontSize: 'var(--text-sm)',
                      fontFamily: 'monospace',
                    }}
                  />
                  <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginTop: '4px' }}>
                    Se informado, sera enviado header X-Webhook-Signature com HMAC-SHA256 do payload
                  </p>
                </div>

                <div style={{ display: 'flex', gap: 'var(--space-4)' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: '500', color: 'var(--text-primary)', marginBottom: '6px' }}>
                      Tentativas
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={10}
                      value={formData.retry_count}
                      onChange={(e) => setFormData({ ...formData, retry_count: parseInt(e.target.value) })}
                      style={{
                        width: '100%',
                        padding: 'var(--space-3)',
                        border: '1px solid var(--border-light)',
                        borderRadius: 'var(--radius-lg)',
                        background: 'var(--bg-primary)',
                        color: 'var(--text-primary)',
                        fontSize: 'var(--text-sm)',
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: '500', color: 'var(--text-primary)', marginBottom: '6px' }}>
                      Timeout (segundos)
                    </label>
                    <input
                      type="number"
                      min={5}
                      max={120}
                      value={formData.timeout_seconds}
                      onChange={(e) => setFormData({ ...formData, timeout_seconds: parseInt(e.target.value) })}
                      style={{
                        width: '100%',
                        padding: 'var(--space-3)',
                        border: '1px solid var(--border-light)',
                        borderRadius: 'var(--radius-lg)',
                        background: 'var(--bg-primary)',
                        color: 'var(--text-primary)',
                        fontSize: 'var(--text-sm)',
                      }}
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  <label htmlFor="is_active" style={{ fontSize: 'var(--text-sm)', color: 'var(--text-primary)' }}>
                    Webhook ativo
                  </label>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end', marginTop: 'var(--space-6)' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false)
                    setShowEditModal(false)
                    resetForm()
                  }}
                  style={{
                    padding: 'var(--space-2) var(--space-4)',
                    border: '1px solid var(--border-light)',
                    borderRadius: 'var(--radius-lg)',
                    background: 'var(--bg-primary)',
                    color: 'var(--text-primary)',
                    fontSize: 'var(--text-sm)',
                    cursor: 'pointer',
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={savingWebhook || formData.events.length === 0}
                  style={{
                    padding: 'var(--space-2) var(--space-4)',
                    background: 'var(--color-primary-600)',
                    color: 'white',
                    border: 'none',
                    borderRadius: 'var(--radius-lg)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: '500',
                    cursor: savingWebhook ? 'not-allowed' : 'pointer',
                    opacity: savingWebhook || formData.events.length === 0 ? 0.5 : 1,
                  }}
                >
                  {savingWebhook ? 'Salvando...' : showEditModal ? 'Salvar Alteracoes' : 'Criar Webhook'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Deliveries Modal */}
      {showDeliveriesModal && selectedWebhook && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
            padding: 'var(--space-4)',
          }}
        >
          <div
            style={{
              background: 'var(--bg-primary)',
              borderRadius: 'var(--radius-xl)',
              width: '100%',
              maxWidth: '800px',
              maxHeight: '90vh',
              overflow: 'auto',
            }}
          >
            <div
              style={{
                padding: 'var(--space-6)',
                borderBottom: '1px solid var(--border-light)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div>
                <h2 style={{ fontSize: 'var(--text-xl)', fontWeight: '600', color: 'var(--text-primary)' }}>
                  Historico de Entregas
                </h2>
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', marginTop: '4px' }}>
                  {selectedWebhook.name}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowDeliveriesModal(false)
                  setSelectedWebhook(null)
                  setDeliveries([])
                }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}
              >
                <X size={24} />
              </button>
            </div>
            <div style={{ padding: 'var(--space-6)' }}>
              {loadingDeliveries ? (
                <div style={{ textAlign: 'center', padding: 'var(--space-8)' }}>
                  <RefreshCw size={32} className="animate-spin" style={{ color: 'var(--color-primary-600)' }} />
                </div>
              ) : deliveries.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 'var(--space-8)', color: 'var(--text-muted)' }}>
                  Nenhuma entrega registrada
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                  {deliveries.map((delivery) => (
                    <div
                      key={delivery.id}
                      style={{
                        padding: 'var(--space-4)',
                        border: '1px solid var(--border-light)',
                        borderRadius: 'var(--radius-lg)',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-2)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                          {getDeliveryStatusBadge(delivery.status)}
                          <span
                            style={{
                              padding: '2px 8px',
                              background: 'var(--color-primary-50)',
                              color: 'var(--color-primary-700)',
                              borderRadius: 'var(--radius-sm)',
                              fontSize: 'var(--text-xs)',
                            }}
                          >
                            {delivery.event}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                          {delivery.duration_ms && (
                            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                              {delivery.duration_ms}ms
                            </span>
                          )}
                          {delivery.response_status_code && (
                            <span
                              style={{
                                padding: '2px 8px',
                                background: delivery.response_status_code < 400 ? 'var(--color-success-50)' : 'var(--color-error-50)',
                                color: delivery.response_status_code < 400 ? 'var(--color-success-700)' : 'var(--color-error-700)',
                                borderRadius: 'var(--radius-sm)',
                                fontSize: 'var(--text-xs)',
                              }}
                            >
                              HTTP {delivery.response_status_code}
                            </span>
                          )}
                          {delivery.status === 'failed' && (
                            <button
                              onClick={() => handleRetryDelivery(delivery.id)}
                              style={{
                                padding: '4px 8px',
                                background: 'var(--color-primary-600)',
                                color: 'white',
                                border: 'none',
                                borderRadius: 'var(--radius-md)',
                                fontSize: 'var(--text-xs)',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                              }}
                            >
                              <RefreshCw size={12} />
                              Reenviar
                            </button>
                          )}
                        </div>
                      </div>
                      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginBottom: 'var(--space-2)' }}>
                        <Clock size={12} style={{ display: 'inline', marginRight: '4px' }} />
                        {new Date(delivery.created_at).toLocaleString('pt-BR')}
                        {delivery.attempt_count > 1 && (
                          <span style={{ marginLeft: '8px' }}>
                            (Tentativa {delivery.attempt_count})
                          </span>
                        )}
                      </div>
                      {delivery.error_message && (
                        <div
                          style={{
                            padding: 'var(--space-2)',
                            background: 'var(--color-error-50)',
                            borderRadius: 'var(--radius-md)',
                            fontSize: 'var(--text-xs)',
                            color: 'var(--color-error-700)',
                          }}
                        >
                          {delivery.error_message}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  )
}

export default OutgoingWebhooks
